
import { t } from "../i18n/index.js";
import { trapFocus } from "./focus-trap.js";

let root: HTMLElement | null = null;
function getRoot(): HTMLElement {
  if (root && document.body.contains(root)) return root;
  const el = document.createElement("div");
  el.className = "dr-overlay-root";
  document.body.appendChild(el);
  root = el;
  return el;
}

let lockCount = 0;
function acquireLock(): void {
  lockCount++;
  if (lockCount === 1) {
    document.documentElement.classList.add("dr-scroll-lock");
    document.body.classList.add("dr-scroll-lock");
  }
}
function releaseLock(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.documentElement.classList.remove("dr-scroll-lock");
    document.body.classList.remove("dr-scroll-lock");
  }
}

interface Stacked {
  close(reason?: "esc"): void;
}
const stack: Stacked[] = [];

function onKeyDown(e: KeyboardEvent): void {
  if (e.key === "Escape" && stack.length > 0) {
    const top = stack[stack.length - 1];
    top?.close("esc");
  }
}
document.addEventListener("keydown", onKeyDown);

export type ModalContent = HTMLElement | string;

export interface ModalAction<R = unknown> {
  label: string;

  className?: string;

  onClick?: (ctx: { close: (value?: R) => void; body: HTMLElement }) => void | Promise<void> | R;

  primary?: boolean;
}

export interface ModalOptions<R = unknown> {
  title?: string;
  content: ModalContent;
  actions?: ModalAction<R>[];

  modalClass?: string;

  closeOnBackdrop?: boolean;

  closeOnEscape?: boolean;

  icon?: string;
}

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}

export interface AlertOptions {
  title: string;
  message: string;
  okText?: string;
}

export interface PromptOptions {
  title: string;
  message?: string;
  placeholder?: string;
  initialValue?: string;
  confirmText?: string;
  cancelText?: string;
  validate?: (value: string) => string | undefined;
}

export function openModal<R = undefined>(options: ModalOptions<R>): Promise<R | undefined> {
  return new Promise<R | undefined>((resolve) => {
    const mount = getRoot();
    const backdrop = document.createElement("div");
    backdrop.className = "dr-backdrop";

    const wrap = document.createElement("div");
    wrap.className = "dr-modal-wrap";

    const modal = document.createElement("div");
    modal.className = "dr-modal" + (options.modalClass ? " " + options.modalClass : "");
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    if (options.title) modal.setAttribute("aria-label", options.title);

    const header = document.createElement("div");
    header.className = "dr-modal__header";
    if (options.icon) {
      const plate = document.createElement("span");
      plate.className = "icon-plate icon-plate--sm dr-modal__plate";
      const icon = document.createElement("i");
      icon.className = options.icon;
      icon.setAttribute("aria-hidden", "true");
      plate.appendChild(icon);
      header.appendChild(plate);
    }
    if (options.title) {
      const h = document.createElement("h2");
      h.className = "dr-modal__title";
      h.textContent = options.title;
      header.appendChild(h);
    }
    const close = document.createElement("button");
    close.type = "button";
    close.className = "dr-modal__close";
    close.setAttribute("aria-label", t("common.close"));
    const closeIcon = document.createElement("i");
    closeIcon.className = "fa-solid fa-xmark";
    closeIcon.setAttribute("aria-hidden", "true");
    close.appendChild(closeIcon);
    header.appendChild(close);
    modal.appendChild(header);

    const body = document.createElement("div");
    body.className = "dr-modal__body";
    if (typeof options.content === "string") {
      const p = document.createElement("p");
      p.textContent = options.content;
      body.appendChild(p);
    } else {
      body.appendChild(options.content);
    }
    modal.appendChild(body);

    const actions = options.actions ?? [];
    if (actions.length > 0) {
      const footer = document.createElement("div");
      footer.className = "dr-modal__footer";
      for (const a of actions) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = a.className ?? "btn";
        btn.textContent = a.label;
        btn.addEventListener("click", () => {
          void runAction(a);
        });
        footer.appendChild(btn);
      }
      modal.appendChild(footer);
    }

    wrap.appendChild(modal);
    mount.appendChild(backdrop);
    mount.appendChild(wrap);

    requestAnimationFrame(() => {
      backdrop.setAttribute("data-state", "open");
      modal.setAttribute("data-state", "open");
    });

    const trap = trapFocus(modal);
    acquireLock();

    let closed = false;
    let result: R | undefined;

    async function runAction(a: ModalAction<R>): Promise<void> {
      if (!a.onClick) {
        closeWith(undefined);
        return;
      }
      const ret = await a.onClick({
        close: (v?: R) => closeWith(v),
        body,
      });
      if (ret !== undefined && !closed) {
        closeWith(ret as R);
      }
    }

    function closeWith(value?: R, _reason?: "esc"): void {
      if (closed) return;
      closed = true;
      result = value;

      const idx = stack.indexOf(entry);
      if (idx >= 0) stack.splice(idx, 1);

      backdrop.removeAttribute("data-state");
      modal.removeAttribute("data-state");

      setTimeout(() => {
        trap.release();
        releaseLock();
        backdrop.remove();
        wrap.remove();
        resolve(result);
      }, 200);
    }

    close.addEventListener("click", () => closeWith(undefined));
    if (options.closeOnBackdrop !== false) {
      backdrop.addEventListener("click", () => closeWith(undefined));
    }

    const onEnter = (e: KeyboardEvent): void => {
      if (e.key !== "Enter") return;
      const target = e.target as HTMLElement | null;
      if (target && target.tagName === "TEXTAREA") return;
      const primary = actions.find((a) => a.primary);
      if (primary) {
        e.preventDefault();
        void runAction(primary);
      }
    };
    modal.addEventListener("keydown", onEnter);

    const entry: Stacked = {
      close(reason) {
        if (options.closeOnEscape === false && reason === "esc") return;
        closeWith(undefined, reason);
      },
    };
    stack.push(entry);
  });
}

export function confirm(options: ConfirmOptions): Promise<boolean> {
  return openModal<boolean>({
    title: options.title,
    content: options.message,
    icon: options.danger ? "fa-solid fa-triangle-exclamation" : "fa-solid fa-circle-question",
    actions: [
      {
        label: options.cancelText ?? t("common.cancel"),
        className: "btn btn--ghost",
        onClick: ({ close }) => close(false),
      },
      {
        label: options.confirmText ?? t("common.confirm"),
        className: options.danger ? "btn dr-btn-danger" : "btn btn--primary",
        primary: true,
        onClick: ({ close }) => close(true),
      },
    ],
  }).then((v) => v === true);
}

export function alert(options: AlertOptions): Promise<void> {
  return openModal({
    title: options.title,
    content: options.message,
    icon: "fa-solid fa-circle-info",
    actions: [
      {
        label: options.okText ?? t("common.ok"),
        className: "btn btn--primary",
        primary: true,
        onClick: ({ close }) => close(),
      },
    ],
  }).then(() => undefined);
}

export function prompt(options: PromptOptions): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const body = document.createElement("div");
    if (options.message) {
      const p = document.createElement("p");
      p.textContent = options.message;
      body.appendChild(p);
    }
    const input = document.createElement("input");
    input.type = "text";
    input.className = "input dr-modal__input";
    input.placeholder = options.placeholder ?? "";
    input.value = options.initialValue ?? "";
    body.appendChild(input);

    const err = document.createElement("div");
    err.className = "dr-modal__error";
    err.setAttribute("role", "alert");
    err.hidden = true;
    body.appendChild(err);

    function validateAndGet(): { ok: true; value: string } | { ok: false; msg: string } {
      const value = input.value;
      if (options.validate) {
        const issue = options.validate(value);
        if (issue) return { ok: false, msg: issue };
      }
      return { ok: true, value };
    }

    input.addEventListener("input", () => {
      err.hidden = true;
    });

    void openModal<string>({
      title: options.title,
      content: body,
      icon: "fa-solid fa-pen-to-square",
      actions: [
        {
          label: options.cancelText ?? t("common.cancel"),
          className: "btn btn--ghost",
          onClick: ({ close }) => close(),
        },
        {
          label: options.confirmText ?? t("common.ok"),
          className: "btn btn--primary",
          primary: true,
          onClick: ({ close }) => {
            const r = validateAndGet();
            if (!r.ok) {
              err.textContent = r.msg;
              err.hidden = false;
              input.focus();
              return;
            }
            close(r.value);
          },
        },
      ],
    }).then((v) => resolve(v === undefined ? null : v));
  });
}

export const modal = { open: openModal };

export type ToastKind = "success" | "error" | "info" | "warn";

export interface ToastOptions {
  duration?: number;
  action?: { label: string; onClick: () => void };
}

let toastStack: HTMLElement | null = null;
function getToastStack(): HTMLElement {
  if (toastStack && document.body.contains(toastStack)) return toastStack;
  const el = document.createElement("div");
  el.className = "dr-toast-stack";
  el.setAttribute("role", "region");
  el.setAttribute("aria-label", "notifications");
  document.body.appendChild(el);
  toastStack = el;
  return el;
}

function toastIcon(kind: ToastKind): string {
  switch (kind) {
    case "success": return "fa-solid fa-circle-check";
    case "error":   return "fa-solid fa-circle-exclamation";
    case "warn":    return "fa-solid fa-triangle-exclamation";
    case "info":    return "fa-solid fa-circle-info";
  }
}

function showToast(kind: ToastKind, message: string, opts: ToastOptions = {}): () => void {
  const stackEl = getToastStack();
  const item = document.createElement("div");
  item.className = `dr-toast dr-toast--${kind}`;
  item.setAttribute("role", kind === "error" ? "alert" : "status");

  const iconEl = document.createElement("i");
  iconEl.className = "dr-toast__icon " + toastIcon(kind);
  iconEl.setAttribute("aria-hidden", "true");
  item.appendChild(iconEl);

  const bodyEl = document.createElement("div");
  bodyEl.className = "dr-toast__body";
  const msg = document.createElement("div");
  msg.className = "dr-toast__msg";
  msg.textContent = message;
  bodyEl.appendChild(msg);
  if (opts.action) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "dr-toast__action";
    btn.textContent = opts.action.label;
    btn.addEventListener("click", () => {
      opts.action?.onClick();
      close();
    });
    bodyEl.appendChild(btn);
  }
  item.appendChild(bodyEl);

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "dr-toast__close";
  closeBtn.setAttribute("aria-label", t("common.close"));
  const xIcon = document.createElement("i");
  xIcon.className = "fa-solid fa-xmark";
  xIcon.setAttribute("aria-hidden", "true");
  closeBtn.appendChild(xIcon);
  item.appendChild(closeBtn);

  stackEl.appendChild(item);
  requestAnimationFrame(() => item.setAttribute("data-state", "open"));

  let timeout: number | undefined;
  function close(): void {
    if (timeout) window.clearTimeout(timeout);
    item.removeAttribute("data-state");
    setTimeout(() => item.remove(), 220);
  }
  closeBtn.addEventListener("click", close);

  const duration = opts.duration ?? (kind === "error" ? 6000 : 3800);
  if (duration > 0) {
    timeout = window.setTimeout(close, duration);
  }
  return close;
}

export const toast = {
  success: (msg: string, opts?: ToastOptions) => showToast("success", msg, opts),
  error:   (msg: string, opts?: ToastOptions) => showToast("error", msg, opts),
  info:    (msg: string, opts?: ToastOptions) => showToast("info", msg, opts),
  warn:    (msg: string, opts?: ToastOptions) => showToast("warn", msg, opts),
};
